import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Settings, Plus, LogOut, Search, X } from 'lucide-react';
import DecisionCard from '@/components/DecisionCard';
import CreateDecisionDialog from '@/components/CreateDecisionDialog';
import { format, parseISO } from 'date-fns';

export default function TimelinePage() {
  const { user, logout, getAuthHeaders, API } = useAuth();
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const fetchDecisions = async (query = '') => {
    try {
      setIsSearching(!!query);
      const url = query 
        ? `${API}/decisions?q=${encodeURIComponent(query)}`
        : `${API}/decisions`;
      const response = await axios.get(url, {
        headers: getAuthHeaders()
      });
      setDecisions(response.data);
    } catch (error) {
      console.error('Error fetching decisions:', error);
      toast.error('Failed to load decisions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDecisions();
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchDecisions(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const clearSearch = () => {
    setSearchQuery('');
  };

  const handleDecisionCreated = (newDecision) => {
    setDecisions([newDecision, ...decisions]);
    setShowCreateDialog(false);
    toast.success('Decision recorded');
  };

  const handleDecisionUpdated = (updatedDecision) => {
    setDecisions(decisions.map(d => 
      d.id === updatedDecision.id ? updatedDecision : d
    ));
  };

  const handleDecisionDeleted = (deletedId) => {
    setDecisions(decisions.filter(d => d.id !== deletedId));
    toast.success('Decision removed');
  };

  // Group decisions by month/year
  const groupedDecisions = decisions.reduce((groups, decision) => {
    const date = parseISO(decision.date);
    const monthYear = format(date, 'MMMM yyyy');
    if (!groups[monthYear]) {
      groups[monthYear] = [];
    }
    groups[monthYear].push(decision);
    return groups;
  }, {});

  return (
    <div className="min-h-screen bg-background" data-testid="timeline-page">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-2xl mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-medium tracking-tight hover:text-accent transition-colors">
            Decided
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/settings">
              <Button variant="ghost" size="icon" data-testid="settings-button">
                <Settings size={18} />
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => { logout(); window.location.href = '/'; }}
              data-testid="logout-button"
            >
              <LogOut size={18} />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-8 py-12">
        {/* Welcome Message */}
        <div className="mb-12 animate-fade-in">
          <p className="text-lg text-muted-foreground leading-relaxed">
            Hello, <span className="text-foreground">{user?.name}</span>. 
            Record your decisions and track what comes next.
          </p>
        </div>

        {/* New Decision Button */}
        <Button
          onClick={() => setShowCreateDialog(true)}
          className="mb-8 rounded-sm btn-animated"
          data-testid="new-decision-button"
        >
          <Plus size={18} className="mr-2" />
          New Decision
        </Button>

        {/* Search */}
        <div className="relative mb-12">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/50" />
          <Input
            type="text"
            placeholder="Search decisions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10 bg-background border-border/50 focus:border-border"
            data-testid="search-input"
          />
          {searchQuery && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/50 hover:text-muted-foreground transition-colors"
              data-testid="clear-search"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Timeline */}
        {loading ? (
          <div className="text-center py-16 text-muted-foreground">
            Loading your decisions...
          </div>
        ) : decisions.length === 0 ? (
          <div className="empty-state animate-fade-in" data-testid="empty-state">
            {isSearching ? (
              <>
                <p className="text-muted-foreground text-lg mb-2">No results found.</p>
                <p className="text-muted-foreground/70">
                  Try a different search term.
                </p>
              </>
            ) : (
              <>
                <p className="text-muted-foreground text-lg mb-2">No decisions yet.</p>
                <p className="text-muted-foreground/70">
                  Start by recording your first decision.
                </p>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-16" data-testid="decisions-timeline">
            {Object.entries(groupedDecisions).map(([monthYear, monthDecisions], groupIndex) => (
              <div 
                key={monthYear} 
                className={`animate-fade-in stagger-${Math.min(groupIndex + 1, 5)}`}
              >
                <h2 className="timeline-date mb-6" data-testid="timeline-month-header">
                  {monthYear}
                </h2>
                <div className="space-y-6">
                  {monthDecisions.map((decision) => (
                    <DecisionCard
                      key={decision.id}
                      decision={decision}
                      onUpdate={handleDecisionUpdated}
                      onDelete={handleDecisionDeleted}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create Decision Dialog */}
      <CreateDecisionDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onCreated={handleDecisionCreated}
      />
    </div>
  );
}
